import React, { useEffect, useRef } from 'react';
import intlTelInput from 'intl-tel-input';
// intl-tel-input 28.x ships under `dist/`, not `build/` (the legacy
// 27.x path); using `dist/` here so CRA webpack resolves the CSS.
import 'intl-tel-input/dist/css/intlTelInput.css';

/**
 * International phone input with country flag dropdown + dial-code prefix.
 * Wraps the `intl-tel-input` library (28.x).
 *
 * Default country: UK/GB because the seller flow is UK-first and most sellers
 * enter local mobile numbers without a country prefix.
 *
 * The `country` prop can override the default — pass the seller's
 * answer to step 4 (US/UK) so the dropdown reflects their location.
 *
 * Validation: the consumer step calls `isValidE164` on the value to
 * check format; the same "Phone number is invalid." message v0 uses
 * is shown on bad input.
 *
 * Returns the full E.164 number to onChange (e.g. "+447911123456"), not
 * the raw display value. That's what the BE wants.
 *
 * The setup effect uses a ref to read `onChange` so the iti instance
 * is created exactly once per mount — re-running it on every onChange
 * change would tear down and recreate the dropdown plugin.
 */
export default function IntlPhoneInput({
  value,
  onChange,
  country = 'gb',
  placeholder,
  invalid = false,
}) {
  const inputRef = useRef(null);
  const itiRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (!inputRef.current) return undefined;
    const iti = intlTelInput(inputRef.current, {
      initialCountry: (country || 'gb').toLowerCase(),
      // Use the bundled utils for parsing/validation. Loaded async so the
      // component still renders if the network request fails — only the
      // validation degrades gracefully.
      loadUtils: () => import('intl-tel-input/dist/js/utils.js'),
      separateDialCode: true,
      countryOrder: ['gb', 'us'],
    });
    itiRef.current = iti;

    const handleInput = () => {
      const num = iti.getNumber ? iti.getNumber() : inputRef.current.value;
      onChangeRef.current(num);
    };
    inputRef.current.addEventListener('input', handleInput);
    inputRef.current.addEventListener('countrychange', handleInput);

    const inputEl = inputRef.current;
    return () => {
      if (inputEl) {
        inputEl.removeEventListener('input', handleInput);
        inputEl.removeEventListener('countrychange', handleInput);
      }
      if (itiRef.current) {
        itiRef.current.destroy();
      }
    };
    // Setup is one-shot per mount. country changes are handled by the
    // separate effect below via setCountry().
  }, []); // eslint-disable-line

  // Reflect external country changes (e.g. step 4 answer feeding back
  // into the dropdown).
  useEffect(() => {
    if (itiRef.current && country) {
      itiRef.current.setCountry(country.toLowerCase());
    }
  }, [country]);

  return (
    <input
      ref={inputRef}
      type="tel"
      defaultValue={value || ''}
      placeholder={placeholder}
      className={invalid ? 'iti-invalid' : ''}
      style={{ width: '100%' }}
    />
  );
}

/**
 * Synchronous helper for consumer steps. Returns true if the input
 * currently has a number that intl-tel-input would consider valid.
 *
 * Loose check; the iti.isValidNumber() is the strict version we don't
 * have access to without holding a ref to the instance. The BE will
 * re-validate, so this just catches obvious mistakes (empty / no +).
 */
export function isValidE164(num) {
  if (!num) return false;
  return /^\+[1-9]\d{6,14}$/.test(num.replace(/\s/g, ''));
}
